import React, { useEffect, useState } from 'react';
import { Button, Grid, MenuItem, TextField } from '@mui/material';
import { MobileDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import dayjs from 'dayjs';
import { useDispatch } from 'react-redux';

import CommonPopUp from '../../../../../components/popup/common-popup';
import { Editor } from '../../../../../components';
import { AdminDashboardService } from '../../../../../core/services/admin-dashboard.service';
import { LocalService } from '../../../../../core/services';
import { FormValidation } from '../../../../../core/utility';
import { Loading } from '../../../../../redux/action';

/**
 * Common campaign details popup used by the admin dashboard.
 * Earlier this markup lived inline inside the campaign verification screen,
 * it is moved here so that the platform > campaign details screen can reuse
 * the same view + edit ability.
 */

const adminDashboardService = new AdminDashboardService();
const localService = new LocalService();
const formValidation = new FormValidation();

interface Props {
  openModal: boolean;
  // campaign detail rows returned by getCampaignDetail
  campaignDetail: any;
  onClose: () => void;
  // called after a successful update so that the parent can refresh its list
  onUpdated?: () => void;
}

const CampaignDetailPopup: React.FC<Props> = (props: Props) => {
  const dispatch = useDispatch();
  const { openModal, campaignDetail, onClose, onUpdated } = props;

  const [performEditCancel, setPerformEditCancel] = useState(false);

  const [campaignFormData, setCampaignFormData] = useState<any>({
    campaign_id: campaignDetail?.[0]?.campaign_id,
    campaign_title: campaignDetail?.[0]?.campaign_title,
    campaign_description: campaignDetail?.[0]?.campaign_description,
    proposal_dt: campaignDetail?.[0]?.proposal_dt,
    go_live_dt: campaignDetail?.[0]?.go_live_dt,
    brand_description: campaignDetail?.[0]?.brand_description,
    brand_web_url: campaignDetail?.[0]?.brand_web_url,
    // influencer gender, budget and comments are editable from the admin dashboard
    influencer_gender: campaignDetail?.[0]?.influencer_gender,
    budget: campaignDetail?.[0]?.budget,
    comments: campaignDetail?.[0]?.comments,
  });

  const [formError, setFormErrors] = useState({
    campaign_title: { status: true, message: '' },
    campaign_description: { status: true, message: '' },
    brand_description: { status: true, message: '' },
    brand_web_url: { status: true, message: '' },
    budget: { status: true, message: '' },
    comments: { status: true, message: '' },
  });

  useEffect(() => {
    if (campaignDetail?.length > 0) {
      setCampaignFormData((prevState: any) => ({
        ...prevState,
        campaign_id: campaignDetail[0]?.campaign_id,
        campaign_title: campaignDetail[0]?.campaign_title,
        campaign_description: campaignDetail[0]?.campaign_description,
        proposal_dt: campaignDetail[0]?.proposal_dt,
        go_live_dt: campaignDetail[0]?.go_live_dt,
        brand_description: campaignDetail[0]?.brand_description,
        brand_web_url: campaignDetail[0]?.brand_web_url,
        influencer_gender: campaignDetail[0]?.influencer_gender,
        budget: campaignDetail[0]?.budget,
        comments: campaignDetail[0]?.comments,
      }));
    }
  }, [campaignDetail, openModal, performEditCancel]);

  function handleClose() {
    setPerformEditCancel(false);
    onClose();
  }

  const handleCampaignDetailFormData = async (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(Loading(true));
    try {
      const isValidCampaignTitle = formValidation.GetNameValidate(campaignFormData?.campaign_title);
      const isValidCampaignDescription = formValidation.getdescriptionIsValid(campaignFormData.campaign_description);
      let isValidBrandWebUrl = { status: true, message: '' };

      if (campaignFormData.brand_web_url && campaignFormData.brand_web_url.trim().length) {
        isValidBrandWebUrl = formValidation.CheckIsValidURL(campaignFormData.brand_web_url);
        setFormErrors((prevData) => ({
          ...prevData,
          brand_web_url: isValidBrandWebUrl,
        }));
      }

      // validating budget: it should be a positive number (budget column is an INT)
      let isValidBudget = { status: true, message: '' };
      const budgetValue = `${campaignFormData.budget ?? ''}`.trim();
      if (budgetValue.length === 0 || !/^\d+$/.test(budgetValue)) {
        isValidBudget = { status: false, message: 'Please enter a valid budget (numbers only)' };
      }

      // validating comments: comments column allows max 1024 characters
      let isValidComments = { status: true, message: '' };
      const commentsValue = `${campaignFormData.comments ?? ''}`;
      if (commentsValue.length > 1024) {
        isValidComments = { status: false, message: 'Comments cannot exceed 1024 characters' };
      }

      setFormErrors((prevData) => ({
        ...prevData,
        campaign_title: isValidCampaignTitle,
        campaign_description: isValidCampaignDescription,
        budget: isValidBudget,
        comments: isValidComments,
      }));

      if (
        isValidCampaignTitle.status &&
        isValidCampaignDescription.status &&
        isValidBrandWebUrl.status &&
        isValidBudget.status &&
        isValidComments.status
      ) {
        const res = await adminDashboardService.updateCampaignDetail(campaignFormData);
        if (res.data.status) {
          localService.toastify('Campaign updated successfully', 'success', 1000);
          setPerformEditCancel(false);
          onClose();
          if (onUpdated) onUpdated();
        } else {
          localService.toastify(res.data.message, 'error', 1000);
          setPerformEditCancel(false);
          onClose();
        }
      }
    } catch (error) {
      localService.toastify('An error occurred while updating campaign detail.', 'error', 1000);
      console.error(`Error updating campaign detail: ${error}`);
    } finally {
      dispatch(Loading(false));
    }
  };

  if (!openModal) return null;

  return (
    <CommonPopUp
      className={`modal-lg  mx-auto`}
      openModal={openModal}
      modalTitle="Campaign Details:"
      onClose={() => handleClose()}>
      <div className="upgrade-premium-list">
        <Grid container rowSpacing={2} spacing={2} padding={2}>
          {campaignDetail && campaignDetail.map((detail: any) => (
            <div className='campaign-detail' key={detail.campaign_id}>
              <h2 className='mt-0 mb-3'>Campaign Details</h2>
              {!performEditCancel && <p><strong>Campaign Title:</strong> {detail.campaign_title}</p>}
              {performEditCancel && (
                <TextField
                  className="input-style my-2 "
                  error={!formError.campaign_title.status}
                  helperText={formError.campaign_title.message}
                  id="campaign_title"
                  name="campaign_title"
                  variant="outlined"
                  value={campaignFormData.campaign_title}
                  onChange={(e) =>
                    setCampaignFormData((prevState: any) => ({
                      ...prevState,
                      campaign_title: e.target.value
                    }))
                  }
                />
              )}
              {!performEditCancel && <p><strong>Description:</strong> <div dangerouslySetInnerHTML={{ __html: detail.campaign_description }} /></p>}
              {performEditCancel && <Grid item xs={12}>
                <Editor
                  editorValues={campaignFormData.campaign_description} // Controlled value
                  getEditorValue={(newValue) =>
                    setCampaignFormData((prevState: any) => ({
                      ...prevState,
                      campaign_description: newValue, // Update on change
                    }))
                  }
                />
                <span className="error">{formError.campaign_description.message}</span>
              </Grid>}
              {!performEditCancel && <p><strong>Brand Description:</strong> {detail.brand_description}</p>}
              {performEditCancel && (
                <TextField
                  className="my-2 "
                  id="outlined-multiline-static"
                  placeholder='About Brand (optional)'
                  multiline
                  fullWidth
                  value={campaignFormData.brand_description}
                  label='About Brand (optional)'
                  onChange={(e) =>
                    setCampaignFormData((prevState: any) => ({
                      ...prevState,
                      brand_description: e.target.value
                    }))
                  }
                  rows={2}
                />
              )}
              {!performEditCancel && <p><strong>Brand/Product Website:</strong> {detail.brand_web_url}</p>}
              {performEditCancel && (
                <TextField
                  className='w-100'
                  onChange={(e) => {
                    setCampaignFormData((prevState: any) => ({
                      ...prevState,
                      brand_web_url: e.target.value
                    }))
                  }}
                  value={campaignFormData.brand_web_url}
                  error={!formError.brand_web_url.status}
                  helperText={
                    formError.brand_web_url.message
                  }
                  size='small'
                  type='text'
                  name='Brand Website URL '
                  label='Brand Website / Campaign Product Link (optional)'
                />
              )}
              <p><strong>Platform:</strong> {detail.campaign_platform}</p>
              {!performEditCancel && <p><strong>Influencer Gender:</strong> {detail.influencer_gender}</p>}
              {performEditCancel && (
                <TextField
                  className='my-2'
                  select
                  fullWidth
                  size='small'
                  id='influencer_gender'
                  name='influencer_gender'
                  label='Influencer Gender'
                  value={campaignFormData.influencer_gender || 'Any'}
                  onChange={(e) =>
                    setCampaignFormData((prevState: any) => ({
                      ...prevState,
                      influencer_gender: e.target.value
                    }))
                  }
                >
                  <MenuItem value='Any'>Any</MenuItem>
                  <MenuItem value='Male'>Male</MenuItem>
                  <MenuItem value='Female'>Female</MenuItem>
                </TextField>
              )}
              {!performEditCancel && <p><strong>Budget:</strong> {detail.budget}</p>}
              {performEditCancel && (
                <TextField
                  className='my-2'
                  fullWidth
                  size='small'
                  id='budget'
                  name='budget'
                  type='number'
                  label='Budget'
                  value={campaignFormData.budget ?? ''}
                  error={!formError.budget.status}
                  helperText={formError.budget.message}
                  onChange={(e) =>
                    setCampaignFormData((prevState: any) => ({
                      ...prevState,
                      budget: e.target.value
                    }))
                  }
                />
              )}
              <p><strong>Objective:</strong> {detail.campaign_objective}</p>
              <p><strong>Type:</strong> {detail.campaign_type}</p>
              <p><strong>Visibility:</strong> {detail.campaign_visibility}</p>
              <p><strong>Cost Model:</strong> {detail.campaign_cost_model}</p>
              {!performEditCancel && <p><strong>Proposal Date:</strong> {detail.proposal_dt ? dayjs(detail.proposal_dt).format('DD/MM/YYYY') : 'Not set'}</p>}
              {!performEditCancel && <p><strong>Go Live Date:</strong> {detail.go_live_dt ? dayjs(detail.go_live_dt).format('DD/MM/YYYY') : 'Not set'}</p>}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                {performEditCancel && (
                  <Grid item xs={12} md={6}>
                    <DemoContainer components={['Proposal Date']}>
                      <MobileDatePicker
                        label="Proposal Date"
                        format="DD-MM-YYYY"
                        value={detail.proposal_dt ? dayjs(detail.proposal_dt) : null}
                        minDate={dayjs()}
                        onChange={(date) =>
                          setCampaignFormData((prev: any) => ({
                            ...prev,
                            proposal_dt: date ? date.format('YYYY-MM-DD') : '',
                          }))
                        }
                        slotProps={{
                          textField: { size: 'small' },
                        }}
                      />
                    </DemoContainer>
                  </Grid>
                )}

                {performEditCancel && (
                  <Grid item xs={12} md={6}>
                    <DemoContainer components={['Go Live Date']}>
                      <MobileDatePicker
                        label="Go Live Date"
                        format="DD-MM-YYYY"
                        value={detail.go_live_dt ? dayjs(detail.go_live_dt) : null}
                        minDate={campaignFormData.proposal_dt ? dayjs(campaignFormData.proposal_dt) : dayjs()}
                        onChange={(date) =>
                          setCampaignFormData((prev: any) => ({
                            ...prev,
                            go_live_dt: date ? date.format('YYYY-MM-DD') : '',
                          }))
                        }
                        slotProps={{
                          textField: { size: 'small' },
                        }}
                      />
                    </DemoContainer>
                  </Grid>
                )}
              </LocalizationProvider>
              <p><strong>Status:</strong> {detail.status}</p>
              {detail.campaign_cover_url && (
                <div>
                  <p><strong>Cover Image:</strong></p>
                  <img src={detail.campaign_cover_url} alt="Campaign Cover" style={{ maxWidth: '350px', maxHeight: '250px', border: '1px solid black', borderRadius: '10px', objectFit: 'contain' }} />
                </div>
              )}
              {!performEditCancel && <p><strong>Comments:</strong> {detail.comments}</p>}
              {performEditCancel && (
                <TextField
                  className='my-2'
                  id='comments'
                  name='comments'
                  label='Comments'
                  placeholder='Comments'
                  multiline
                  fullWidth
                  rows={2}
                  value={campaignFormData.comments ?? ''}
                  error={!formError.comments.status}
                  helperText={formError.comments.message}
                  onChange={(e) =>
                    setCampaignFormData((prevState: any) => ({
                      ...prevState,
                      comments: e.target.value
                    }))
                  }
                />
              )}
              <p><strong>Posted At:</strong> {detail.posted_at}</p>
              <p><strong>Remark:</strong> {detail.remark}</p>
              <p><strong>Brand:</strong> {detail.brand}</p>
              <p><strong>Product Name:</strong> {detail.product_name}</p>
              <p><strong>Product Description:</strong> {detail.product_description}</p>
              <p><strong>Product Cost:</strong> {detail.product_cost}</p>
              {detail.product_image_url && (
                <div >
                  <p><strong>Product Image:</strong></p>
                  <img src={detail.product_image_url} alt="Product" style={{ maxWidth: '350px', maxHeight: '250px', border: '1px solid black', borderRadius: '10px', objectFit: 'contain' }} />
                </div>
              )}
              <p><strong>Product Link:</strong> {detail.product_link}</p>
            </div>
          ))}
        </Grid>
      </div>
      <div className="popup-footer">
        <div className="row justify-content-end">
          {!performEditCancel && (
            <div className="col-auto px-0 ms-2">
              <Button
                className="btn btn-primary"
                onClick={() => setPerformEditCancel(true)}
                variant="contained"
              >
                Edit
              </Button>
            </div>
          )}
          {performEditCancel && (
            <>
              <div className="col-auto px-0">
                <Button
                  className=" btn btn-outline-primary"
                  onClick={() => setPerformEditCancel(false)}
                  variant="contained"
                >
                  Cancel
                </Button>
              </div>
              <div className="col-auto px-0 ms-2">
                <Button
                  className="btn btn-primary"
                  onClick={handleCampaignDetailFormData}
                  variant="contained"
                >
                  Save
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </CommonPopUp>
  );
};

export { CampaignDetailPopup };
